import { useState } from "react";
import { Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { importStudentsCsv } from "@/lib/api";

const TEMPLATE = "prenom;nom;classe;email;annee;date_naissance;assurance_rc;referent_pedagogique_nom;referent_pedagogique_email;representant_legal_prenom;representant_legal_nom;representant_legal_email;representant_legal_telephone;entreprise;tuteur;email_tuteur;tuteur_fonction;tuteur_telephone;ville;adresse_complete;siret;effectif;secteur;date_debut_stage;date_fin_stage;rythme;duree_hebdomadaire_heures;modalites_encadrement;gratification_taux_eur_h;gratification_due\nCamille;Durand;BTS SAM 1;camille.durand@bts.fr;1A;2008-04-12;oui;Claire Martin;professeur@bts.fr;Marie;Durand;marie.durand@example.fr;0694000000;Entreprise Exemple;Aline Bernard;aline.bernard@example.fr;Responsable service;0594000000;Cayenne;12 rue Exemple;12345678900011;18;Services;2026-03-02;2026-04-10;Lun-Ven;35;visites sur le terrain, visioconférence;4.50;non";

export default function ImportCsvDialog({ professorId, onImported }) {
  const [open, setOpen] = useState(false);
  const [csvContent, setCsvContent] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modele-import-etudiants-stages.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const readFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvContent(String(reader.result || ""));
    reader.readAsText(file, "utf-8");
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const imported = await importStudentsCsv({ csv_content: csvContent, professor_id: professorId });
      setResult(imported);
      await onImported();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-none" data-testid="csv-import-open-button"><Upload className="h-4 w-4" />Importer CSV</Button></DialogTrigger>
      <DialogContent className="rounded-none" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-testid="csv-import-title">Importer étudiants & stages</DialogTitle>
          <DialogDescription data-testid="csv-import-description">Chargez un fichier CSV avec étudiants, entreprise, tuteur et dates de stage.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="csv-import-form">
          <div className="flex flex-wrap gap-2" data-testid="csv-import-actions">
            <Button type="button" variant="outline" onClick={downloadTemplate} className="rounded-none" data-testid="csv-template-download-button"><Download className="h-4 w-4" />Modèle CSV</Button>
            <input type="file" accept=".csv,text/csv" onChange={readFile} className="w-full border border-zinc-200 p-2 text-sm" data-testid="csv-file-input" />
          </div>
          <Textarea value={csvContent} onChange={(event) => setCsvContent(event.target.value)} className="min-h-44 rounded-none font-mono text-xs" placeholder={TEMPLATE} data-testid="csv-content-textarea" />
          <Button type="submit" disabled={loading || !csvContent.trim()} className="w-full rounded-none bg-[#002FA7]" data-testid="csv-import-submit-button">{loading ? "Import en cours…" : "Importer"}</Button>
        </form>
        {result && <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="csv-import-result"><p data-testid="csv-import-result-summary">{result.created_students} étudiant(s), {result.created_companies} entreprise(s), {result.created_internships} stage(s) créé(s).</p>{result.skipped_rows.length > 0 && <ul className="mt-2 list-disc pl-5" data-testid="csv-import-skipped-list">{result.skipped_rows.map((item) => <li key={item} data-testid={`csv-import-skipped-${item.replaceAll(" ", "-")}`}>{item}</li>)}</ul>}</div>}
        <Button type="button" variant="outline" onClick={() => setOpen(false)} className="w-full rounded-none" data-testid="csv-import-close-button">Fermer</Button>
      </DialogContent>
    </Dialog>
  );
}